import { useEffect, useMemo, useState } from "react";
import { Button } from "../ui";
import { formatSar } from "../../utils/billingMoney";
import {
  createSelfServiceSubscriptionAPI,
  previewSelfServiceSubscriptionAPI,
  schedulePlanChangeAPI,
  previewPlanChangeAPI,
} from "../../utils/api/billing";
import { fillTemplate } from "./clientBillingText";

// Covers both new subscribe (LUD or INTG) and LUD upgrade, per v141's
// subRequestModal: branch selection -> server-computed price preview ->
// "إصدار الفاتورة" issues an unpaid invoice. The subscription itself only
// activates once Finance approves the payment (enforced server-side).
const SubscribeModal = ({
  mode, // "subscribe" | "upgrade"
  type, // "platform" | "integration"
  plan,
  currentSubscriptionId,
  branches,
  eligibleBranchIds,
  lang,
  text,
  onCancel,
  onDone,
}) => {
  const token = localStorage.getItem("auth-token");
  const isRtl = lang !== "en";
  const eligibleSet = useMemo(
    () => (type === "integration" ? new Set(eligibleBranchIds || []) : null),
    [type, eligibleBranchIds],
  );
  const selectableBranches = branches || [];
  const [selectedBranchIds, setSelectedBranchIds] = useState(() =>
    mode === "upgrade" ? (branches || []).map((branch) => String(branch.branchId || branch._id)) : [],
  );
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const toggleBranch = (branchId) => {
    if (mode === "upgrade") return; // upgrade is account-wide, selection is locked
    setSelectedBranchIds((current) =>
      current.includes(branchId)
        ? current.filter((id) => id !== branchId)
        : [...current, branchId],
    );
  };

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!selectedBranchIds.length) {
        setPreview(null);
        return;
      }
      setPreviewLoading(true);
      setError("");
      try {
        const result =
          mode === "upgrade"
            ? await previewPlanChangeAPI(token, currentSubscriptionId, {
                targetPlanId: plan._id,
                branchIds: selectedBranchIds,
              })
            : await previewSelfServiceSubscriptionAPI(token, {
                type,
                planId: plan._id,
                branchIds: selectedBranchIds,
              });
        if (!cancelled) setPreview(result.data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranchIds.join(","), plan._id]);

  const submit = async () => {
    if (submitting || !selectedBranchIds.length) return;
    setSubmitting(true);
    setError("");
    try {
      if (mode === "upgrade") {
        await schedulePlanChangeAPI(token, currentSubscriptionId, {
          targetPlanId: plan._id,
          branchIds: selectedBranchIds,
        });
      } else {
        await createSelfServiceSubscriptionAPI(token, {
          type,
          planId: plan._id,
          branchIds: selectedBranchIds,
        });
      }
      await onDone();
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  const pricing = preview?.pricingPreview;
  const title =
    mode === "upgrade"
      ? fillTemplate(text.upgradeModalTitle, { package: plan.code })
      : type === "integration"
        ? text.intgSubscribeModalTitle
        : fillTemplate(text.subscribeModalTitle, { package: plan.code });

  return (
    <div className="billing-modal-backdrop" dir={isRtl ? "rtl" : "ltr"}>
      <div className="billing-modal">
        <h3>{title}</h3>
        {error && <div className="billing-error">{error}</div>}

        <strong>{text.selectBranches}</strong>
        <div className="cb-branch-picker">
          {selectableBranches.length === 0 && (
            <span>{text.noEligibleBranches}</span>
          )}
          {selectableBranches.map((branch) => {
            const branchId = String(branch.branchId || branch._id);
            const ineligible = type === "integration" && eligibleSet && !eligibleSet.has(branchId);
            return (
              <label key={branchId} className={ineligible ? "disabled" : ""}>
                <input
                  type="checkbox"
                  disabled={mode === "upgrade" || ineligible}
                  checked={selectedBranchIds.includes(branchId)}
                  onChange={() => toggleBranch(branchId)}
                />
                {branch.name || branch.branchName}
                {ineligible ? ` — ${text.ineligibleForIntg}` : ""}
              </label>
            );
          })}
        </div>

        {previewLoading && <div className="cb-loading">{text.loading}</div>}
        {pricing && (
          <div className="billing-preview-box">
            <div>
              <span>{text.subtotal}</span>
              <strong>{formatSar(pricing.subtotalMinor, lang)}</strong>
            </div>
            <div>
              <span>{text.vat}</span>
              <strong>{formatSar(pricing.vatAmountMinor, lang)}</strong>
            </div>
            <div>
              <span>{text.total}</span>
              <strong>{formatSar(pricing.totalMinor, lang)}</strong>
            </div>
          </div>
        )}

        <div className="billing-modal-actions">
          <Button
            appearance="filled"
            loading={submitting}
            disabled={submitting || !selectedBranchIds.length || previewLoading}
            onClick={submit}
          >
            {submitting ? text.submitting : text.confirmSubscribe}
          </Button>
          <Button appearance="ghost" disabled={submitting} onClick={onCancel}>
            {text.cancel}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SubscribeModal;
